<script setup>
import DataTable from "@/components/global-themed/table/DataTable.vue";
import TableHeaderRow from "@/components/global-themed/table/TableHeaderRow.vue";
import TableHeaderCell from "@/components/global-themed/table/TableHeaderCell.vue";
import {useStore} from "vuex";
import {computed} from "vue";
import useDates from "@/composables/useDates";
import TableRow from "@/components/global-themed/table/TableRow.vue";
import TableCell from "@/components/global-themed/table/TableCell.vue";
import HintSquare from "@/components/global-themed/HintSquare.vue";
import TableBody from "@/components/global-themed/table/TableBody.vue";
import HistoricDiffTag from "@/components/competition/partials/historic/HistoricDiffTag.vue";

const store = useStore();

const isKeywordView = computed(() => store.getters['competitionV2/comparisonType'] === 'keyword')

const history = computed(() => store.getters["competitionV2/filteredHistory"]);

const historyDates = computed(() => history.value.map(h => h.date));

const dates = useDates();

</script>

<template>
  <DataTable>
    <TableHeaderRow>
      <TableHeaderCell
        col-span="4"
      >
        Keywords
      </TableHeaderCell>
      <TableHeaderCell
        v-for="date in historyDates"
        :key="date"
        col-span="2"
      >
        {{ dates.formattedDate(date) }}
      </TableHeaderCell>
    </TableHeaderRow>
    <TableBody
      v-if="isKeywordView"
      class="tw-max-h-[70vh]"
    >
      <TableRow>
        <TableCell
          col-span="4"
          class="tw-flex tw-items-center tw-gap-2"
        >
          <span>Position 1 Keywords</span>
          <HintSquare
            hint="The total number of keywords we monitor, showing where the selected website ranks in the first position of Google search results."
          />
        </TableCell>
        <TableCell
          v-for="(h, i) in history"
          :key="h.id"
          col-span="2"
          class="tw-font-medium"
        >
          {{ h.pos1?.toLocaleString() ?? 'N/A' }}
          <HistoricDiffTag
            v-if="i !== 0"
            :value="h.pos1"
            name="pos1"
          />
        </TableCell>
      </TableRow>

      <TableRow>
        <TableCell
          col-span="4"
          class="tw-flex tw-items-center tw-gap-2"
        >
          <span>Position 1-3 Keywords</span>
          <HintSquare
            hint="The total number of keywords we monitor, showing where the selected website ranks within the first 3 positions of Google search results."
          />
        </TableCell>
        <TableCell
          v-for="(h, i) in history"
          :key="h.id"
          col-span="2"
        >
          {{ h.top3?.toLocaleString() ?? 'N/A' }}
          <HistoricDiffTag
            v-if="i !== 0"
            :value="h.top3"
            name="top3"
          />
        </TableCell>
      </TableRow>

      <TableRow>
        <TableCell
          col-span="4"
          class="tw-flex tw-items-center tw-gap-2"
        >
          <span>Page 1 Keywords</span>
          <HintSquare
            hint="The total number of keywords we monitor, showing where the selected website ranks on the first page of Google search results. This also includes the count of keywords in position 1-3."
          />
        </TableCell>
        <TableCell
          v-for="(h, i) in history"
          :key="h.id"
          col-span="2"
        >
          {{ h.page1?.toLocaleString() ?? 'N/A' }}
          <HistoricDiffTag
            v-if="i !== 0"
            :value="h.page1"
            name="page1"
          />
        </TableCell>
      </TableRow>

      <TableRow>
        <TableCell
          col-span="4"
          class="tw-flex tw-items-center tw-gap-2"
        >
          <span>Page 2 Keywords</span>
          <HintSquare
            hint="The total number of keywords we monitor, showing where the selected website ranks on the second page of Google search results."
          />
        </TableCell>
        <TableCell
          v-for="(h, i) in history"
          :key="h.id"
          col-span="2"
        >
          {{ h.page2?.toLocaleString() ?? 'N/A' }}
          <HistoricDiffTag
            v-if="i !== 0"
            :value="h.page2"
            name="page2"
          />
        </TableCell>
      </TableRow>

      <TableRow>
        <TableCell
          col-span="4"
          class="tw-flex tw-items-center tw-gap-2"
        >
          <span>Total Keywords</span>
          <HintSquare
            hint="The total number of keywords we monitor, showing where the selected website ranks within the first 10 pages of Google search results."
          />
        </TableCell>
        <TableCell
          v-for="(h, i) in history"
          :key="h.id"
          col-span="2"
        >
          {{ h.totalKeywords?.toLocaleString() ?? 'N/A' }}
          <HistoricDiffTag
            v-if="i !== 0"
            :value="h.totalKeywords"
            name="totalKeywords"
          />
        </TableCell>
      </TableRow>
    </TableBody>

    <TableBody v-if="!isKeywordView">
      <TableRow>
        <TableCell
          col-span="4"
          class="tw-flex tw-items-center tw-gap-2"
        >
          <span>Total Backlinks</span>
          <HintSquare
            hint="This represents the number of individual backlinks directing users to the selected website."
          />
        </TableCell>
        <TableCell
          v-for="(h, i) in history"
          :key="h.id"
          col-span="2"
        >
          {{ h.totalBacklinks?.toLocaleString() ?? 'N/A' }}
          <HistoricDiffTag
            v-if="i !== 0"
            :value="h.totalBacklinks"
            name="totalBacklinks"
          />
        </TableCell>
      </TableRow>

      <TableRow>
        <TableCell
          col-span="4"
          class="tw-flex tw-items-center tw-gap-2"
        >
          <span>Referring Domains</span>
          <HintSquare
            hint="This is the count of unique domains that each host at least one backlink pointing to the selected website."
          />
        </TableCell>
        <TableCell
          v-for="(h, i) in history"
          :key="h.id"
          col-span="2"
        >
          {{ h.referringDomains?.toLocaleString() ?? 'N/A' }}
          <HistoricDiffTag
            v-if="i !== 0"
            :value="h.referringDomains"
            name="referringDomains"
          />
        </TableCell>
      </TableRow>

      <TableRow>
        <TableCell
          col-span="4"
          class="tw-flex tw-items-center tw-gap-2"
        >
          <span>Trust Flow</span>
          <HintSquare
            hint="Trust Flow is a metric that measures the quality and trustworthiness of a website based on the credibility of sites that link to it."
          />
        </TableCell>
        <TableCell
          v-for="(h, i) in history"
          :key="h.id"
          col-span="2"
        >
          {{ h.trustFlow?.toLocaleString() ?? 'N/A' }}
          <HistoricDiffTag
            v-if="i !== 0"
            :value="h.trustFlow"
            name="trustFlow"
          />
        </TableCell>
      </TableRow>

      <TableRow>
        <TableCell
          col-span="4"
          class="tw-flex tw-items-center tw-gap-2"
        >
          <span>Citation Flow</span>
          <HintSquare
            hint="Citation Flow quantifies the influence or authority of a website by counting the number of sites linking to it, regardless of the quality of these links."
          />
        </TableCell>
        <TableCell
          v-for="(h, i) in history"
          :key="h.id"
          col-span="2"
        >
          {{ h.citationFlow?.toLocaleString() ?? 'N/A' }}
          <HistoricDiffTag
            v-if="i !== 0"
            :value="h.citationFlow"
            name="citationFlow"
          />
        </TableCell>
      </TableRow>

      <TableRow>
        <TableCell
          col-span="4"
          class="tw-flex tw-items-center tw-gap-2"
        >
          <span>Domain Authority</span>
          <HintSquare
            hint="Our Domain Authority score is calculated using proprietary metrics that assess the overall credibility and influence of a website based on its web of backlinks."
          />
        </TableCell>
        <TableCell
          v-for="(h, i) in history"
          :key="h.id"
          col-span="2"
        >
          {{ h.domainAuthority?.toLocaleString() ?? 'N/A' }}
          <HistoricDiffTag
            v-if="i !== 0"
            :value="h.domainAuthority"
            name="domainAuthority"
          />
        </TableCell>
      </TableRow>
    </TableBody>
  </DataTable>
</template>